// Save / Share / Export state for the Colored Text and Gradient generators. Both save as the
// `color_code` saved-element type; `k` in the hash says which page opens it.
//
// Everything decoded here is UNTRUSTED (a share link, a saved row, an imported file): each field is
// checked on its own and DROPPED when bad, so the page keeps its default for it.
import { unicodeAtob, unicodeBtoa } from '@/app/lib/base64';
import { HEX_COLOR } from './gradient';

// Both editors are single-line fields: no control characters, no line/paragraph separators.
const CONTROL_CHARS = new RegExp('[\\u0000-\\u001F\\u007F-\\u009F\\u2028\\u2029]', 'g');
const singleLine = (v: unknown, max: number): string | undefined =>
  typeof v === 'string' ? v.replace(CONTROL_CHARS, '').slice(0, max) : undefined;

export const TEXT_MAX = 1000;
export const GRADIENT_TEXT_MAX = 500;
export const MAX_STOPS = 10;

// ── Colored text ─────────────────────────────────────────────────────────────

export type SectionTarget = 'vanilla' | 'spigot';
export type AmpHex = 'hash' | 'legacy';

export interface ColoredTextState {
  text: string;            // &-coded, as RichTextInput emits it
  sectionTarget: SectionTarget;
  ampHex: AmpHex;
}

interface TextCompact { v: 1; t: string; s: SectionTarget; a: AmpHex }

export function encodeColoredText(s: ColoredTextState): string {
  const data: TextCompact = { v: 1, t: s.text, s: s.sectionTarget, a: s.ampHex };
  return unicodeBtoa(JSON.stringify(data));
}

function sanitizeColoredText(data: Record<string, unknown>): Partial<ColoredTextState> {
  const out: Partial<ColoredTextState> = {};
  const text = singleLine(data.t, TEXT_MAX);
  if (text !== undefined) out.text = text;
  if (data.s === 'spigot' || data.s === 'vanilla') out.sectionTarget = data.s;
  if (data.a === 'legacy' || data.a === 'hash') out.ampHex = data.a;
  return out;
}

// ── Gradient ─────────────────────────────────────────────────────────────────

export interface GradientState {
  text: string;
  stops: string[];         // #RRGGBB, 2..MAX_STOPS
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  skipSpaces: boolean;
}

interface GradientCompact { v: 1; k: 'g'; t: string; c: string[]; f?: string; sp?: 0 }
const FLAG_LETTERS: [keyof GradientState, string][] = [['bold', 'l'], ['italic', 'o'], ['underline', 'n'], ['strike', 'm']];

export function encodeGradient(s: GradientState): string {
  const data: GradientCompact = { v: 1, k: 'g', t: s.text, c: s.stops };
  const f = FLAG_LETTERS.filter(([k]) => s[k]).map(([, l]) => l).join('');
  if (f) data.f = f;
  if (!s.skipSpaces) data.sp = 0;
  return unicodeBtoa(JSON.stringify(data));
}

function sanitizeGradient(data: Record<string, unknown>): Partial<GradientState> {
  const out: Partial<GradientState> = {};
  const text = singleLine(data.t, GRADIENT_TEXT_MAX);
  if (text !== undefined) out.text = text;
  if (Array.isArray(data.c)) {
    const stops = data.c.filter((c): c is string => typeof c === 'string' && HEX_COLOR.test(c)).slice(0, MAX_STOPS).map(c => c.toUpperCase());
    if (stops.length >= 2) out.stops = stops;
  }
  if (typeof data.f === 'string') {
    for (const [k, l] of FLAG_LETTERS) (out as Record<string, boolean>)[k] = data.f.includes(l);
  }
  if (data.sp === 0) out.skipSpaces = false;
  return out;
}

// ── Decoding ─────────────────────────────────────────────────────────────────

export type DecodedTextTool =
  | { kind: 'text'; state: Partial<ColoredTextState> }
  | { kind: 'gradient'; state: Partial<GradientState> };

function decodeObject(data: unknown): DecodedTextTool | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  if (d.v !== 1) return null;
  return d.k === 'g' ? { kind: 'gradient', state: sanitizeGradient(d) } : { kind: 'text', state: sanitizeColoredText(d) };
}

export function decodeTextToolHash(hash: string): DecodedTextTool | null {
  try {
    return decodeObject(JSON.parse(unicodeAtob(hash)));
  } catch {
    return null;
  }
}

/** The page a saved `color_code` opens on. */
export const COLORED_TEXT_ROUTE = '/tools/colored-text-generator';
export const GRADIENT_ROUTE = '/tools/gradient-generator';
export function routeForTextToolHash(hash: string): string {
  return decodeTextToolHash(hash)?.kind === 'gradient' ? GRADIENT_ROUTE : COLORED_TEXT_ROUTE;
}

// ── JSON file export / import ───────────────────────────────────────────────

export function exportTextToolJSON(hash: string): string {
  const decoded = decodeTextToolHash(hash);
  return JSON.stringify({ version: 1, tool: decoded?.kind === 'gradient' ? 'gradient-generator' : 'colored-text-generator', state: JSON.parse(unicodeAtob(hash)) }, null, 2);
}

export function importTextToolJSON(json: string): DecodedTextTool | null {
  try {
    const file = JSON.parse(json) as { version?: unknown; state?: unknown };
    if (file?.version !== 1) return null;
    return decodeObject(file.state);
  } catch {
    return null;
  }
}
